import { z } from 'zod';

/**
 * Login-Flow (Modul auth, ADR 0012 + ADR 0034): Passwort → zweiter Faktor
 * (Pflicht). Zweiter Faktor ist TOTP ODER Passkey (WebAuthn) — mindestens
 * einer der beiden muss eingerichtet sein, keiner ist Default.
 */

export const loginSchema = z.object({
  email: z.string().trim().email().max(200),
  password: z.string().min(1).max(200),
});
export type LoginInput = z.infer<typeof loginSchema>;

/**
 * Antwort auf den Passwort-Schritt:
 * - TOTP_REQUIRED — nur TOTP eingerichtet, Code abfragen.
 * - PASSKEY_REQUIRED — nur Passkey(s) eingerichtet, Passkey-Bestätigung abfragen.
 * - SECOND_FACTOR_CHOICE — beide eingerichtet, Nutzer wählt im UI.
 * - SETUP_REQUIRED — noch kein zweiter Faktor eingerichtet, Nutzer wählt beim
 *   Ersteinrichten frei zwischen TOTP und Passkey (ADR 0034).
 * `pendingToken` ist kurzlebig (10 Min) und deckt beide Methoden ab.
 */
export const loginResponseSchema = z.object({
  status: z.enum(['TOTP_REQUIRED', 'PASSKEY_REQUIRED', 'SECOND_FACTOR_CHOICE', 'SETUP_REQUIRED']),
  pendingToken: z.string(),
});
export type LoginResponse = z.infer<typeof loginResponseSchema>;

export const totpSetupRequestSchema = z.object({
  pendingToken: z.string(),
});
export type TotpSetupRequest = z.infer<typeof totpSetupRequestSchema>;

/** Geheimnis für die Authenticator-App (Base32) + otpauth-URL für den QR-Code. */
export const totpSetupResponseSchema = z.object({
  secret: z.string(),
  otpauthUrl: z.string(),
});
export type TotpSetupResponse = z.infer<typeof totpSetupResponseSchema>;

export const totpVerifySchema = z.object({
  pendingToken: z.string(),
  code: z
    .string()
    .trim()
    .regex(/^\d{6}$/, 'Der Code besteht aus 6 Ziffern.'),
});
export type TotpVerifyInput = z.infer<typeof totpVerifySchema>;

/**
 * Bestätigung der TOTP-Self-Service-Einrichtung (ADR 0034): nur der Code, KEIN
 * Pending-Token — läuft aus einer authentifizierten Sitzung (Sicherheits-
 * einstellungen), z. B. um TOTP zusätzlich zu einem Passkey einzurichten.
 */
export const totpEnrollSchema = z.object({
  code: z
    .string()
    .trim()
    .regex(/^\d{6}$/, 'Der Code besteht aus 6 Ziffern.'),
});
export type TotpEnrollInput = z.infer<typeof totpEnrollSchema>;

/** Erfolgreiche Anmeldung: Access-Token für den Authorization-Header. */
export const sessionResponseSchema = z.object({
  accessToken: z.string(),
  expiresInSeconds: z.number().int(),
});
export type SessionResponse = z.infer<typeof sessionResponseSchema>;

export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1).max(200),
  newPassword: z
    .string()
    .min(10, 'Mindestens 10 Zeichen.')
    .max(200),
});
export type ChangePasswordInput = z.infer<typeof changePasswordSchema>;

/**
 * Admin setzt ein (neues) Passwort für einen Benutzer; optional wird der GESAMTE
 * zweite Faktor zurückgesetzt (TOTP UND alle Passkeys) — z. B. bei Geräteverlust,
 * damit sich der Nutzer beim nächsten Login neu einrichten kann.
 */
export const setPasswordSchema = z.object({
  newPassword: z.string().min(10, 'Mindestens 10 Zeichen.').max(200),
  resetSecondFactor: z.boolean().default(false),
});
export type SetPasswordInput = z.infer<typeof setPasswordSchema>;
